import { ReactElement, useState } from 'react';
import { useToast } from '@hooks';
import makeDefaultExpression from '@/helpers/makeDefaultExpression';
import Modal from '@components/Modal';
import Button from '@components/DesignLibrary/Button';
import { Questionnaire } from '@/typings/Questionnaire';
import Spacer from '@/components/Spacer';
import useNoodleApi from '@/hooks/useNoodleApi';
import { createExpression } from '@/tsClient/expressions';
import ExpressionEditor from '@/components/ExpressionEditor';

import s from './AddConditionsModal.module.scss';

type Expression = Pick<Questionnaire.Expression, 'expression' | 'filterExpression' | 'filterExpressionVariableName' | 'filterExpressionOperand' | 'filterExpressionItemSelector' | 'filterExpressionReducer'> & {
  variables: Pick<Questionnaire.ExpressionVariable, 'variableName' | 'referenceId' | 'referenceType' | 'selector'>[];
};

type Props = {
  stepId?: string | null;
  numConditions: number;
  conditionExpression: Expression | null;
  options: { key: string; label: string }[];
  type: 'workflow-context';
  onClose: () => void;
  onSubmit: (expressionId: string) => void;
};

const AddConditionsModal = ({ conditionExpression, numConditions, options, type, onClose, onSubmit }: Props): ReactElement => {
  const [isSaving, setIsSaving] = useState(false);

  const [currentConditionExpression, setConditionExpression] = useState<Expression>(conditionExpression || makeDefaultExpression());

  const { getData: createExpressionFn } = useNoodleApi(createExpression, {
    toastOnError: true,
    toastSuccessMessage: () => [useToast.ToastTypeVariants.SUCCESS, 'Conditions created successfully!'],
  });

  const handleSaveConditions = async (): Promise<void> => {
    setIsSaving(true);
    const data = await createExpressionFn({
      expression: currentConditionExpression,
    });
    const expressionId = data.data?.expressionId;
    if (expressionId) {
      onSubmit(expressionId);
    }
    onClose();
    setIsSaving(false);
  };

  const addContextPrefix = (fieldKey: string, prefix?: string): string =>
    prefix
      ? `${prefix}${fieldKey
        .split('-')
        .map(word => word.charAt(0).toUpperCase() + word.slice(1))
        .join('')}`
      : fieldKey;

  const fieldsInfo = options.reduce((acc, option) => {
    const fieldKey = addContextPrefix(option.key, type === 'workflow-context' ? 'context' : undefined);
    acc[fieldKey] = {
      key: fieldKey,
      label: fieldKey,
      selector: type === 'workflow-context' ? option.key : undefined,
      type,
    };
    return acc;
  }, {} as Record<string, { key: string; label: string; type: string; selector?: string }>);

  return (
    <Modal hideBottomInset title={numConditions > 0 ? 'Edit conditions' : 'Add conditions'} onClose={onClose}>
      <div className={s.addConditions}>
        <ExpressionEditor
          expression={currentConditionExpression}
          fieldsInfo={{
            ...fieldsInfo,
            ...(type === 'workflow-context' && {
              context: {
                key: 'context',
                label: 'context',
                type: 'workflow-context',
              },
            }),
          }}
          onChange={setConditionExpression}
          canEditVars
        />
        <Spacer size={12} />
        <Button variant="primary" size="md" fullWidth onClick={handleSaveConditions} loading={isSaving}>
          Submit
        </Button>
      </div>
    </Modal>
  );
};

export default AddConditionsModal;
